import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import {
  LayoutTemplate, Type, Image as ImageIcon, MousePointerClick,
  Minus, Columns2, GripVertical, Trash2, ChevronUp, ChevronDown
} from 'lucide-react';
import { Button } from '../../ui/button';

const ICONS = { header: LayoutTemplate, text: Type, image: ImageIcon, button: MousePointerClick, separator: Minus, columns: Columns2 };
const LABELS = { header: 'Intestazione', text: 'Testo', image: 'Immagine', button: 'Bottone', separator: 'Separatore', columns: 'Colonne' };

const ColorInput = ({ value, onChange, label }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-8 h-8 rounded cursor-pointer border border-border" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs font-mono" />
    </div>
  </div>
);

const HeaderEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <div className="space-y-1">
      <Label className="text-xs">Titolo</Label>
      <Input value={data.title} onChange={(e) => onChange({ ...data, title: e.target.value })} placeholder="Titolo" data-testid="block-header-title" />
    </div>
    <div className="space-y-1">
      <Label className="text-xs">Sottotitolo</Label>
      <Input value={data.subtitle} onChange={(e) => onChange({ ...data, subtitle: e.target.value })} placeholder="Sottotitolo (opzionale)" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <ColorInput label="Sfondo" value={data.bgColor} onChange={(v) => onChange({ ...data, bgColor: v })} />
      <ColorInput label="Testo" value={data.textColor} onChange={(v) => onChange({ ...data, textColor: v })} />
    </div>
    <div className="flex items-center gap-2">
      <Switch checked={data.showLogo} onCheckedChange={(v) => onChange({ ...data, showLogo: v })} />
      <Label className="text-xs">Mostra logo</Label>
    </div>
    {data.showLogo && (
      <div className="space-y-1">
        <Label className="text-xs">URL Logo</Label>
        <Input value={data.logoUrl} onChange={(e) => onChange({ ...data, logoUrl: e.target.value })} placeholder="https://..." />
      </div>
    )}
  </div>
);

const TextEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <div className="space-y-1">
      <Label className="text-xs">Contenuto</Label>
      <Textarea value={data.content} onChange={(e) => onChange({ ...data, content: e.target.value })} rows={3} placeholder="Scrivi il testo..." data-testid="block-text-content" />
      <p className="text-[10px] text-muted-foreground">Supporta HTML base: &lt;strong&gt;, &lt;em&gt;, &lt;a&gt;, &lt;br/&gt;</p>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">Allineamento</Label>
        <Select value={data.align} onValueChange={(v) => onChange({ ...data, align: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Sinistra</SelectItem>
            <SelectItem value="center">Centro</SelectItem>
            <SelectItem value="right">Destra</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Dimensione (px)</Label>
        <Input type="number" min="12" max="32" value={data.fontSize} onChange={(e) => onChange({ ...data, fontSize: e.target.value })} className="h-8 text-xs" />
      </div>
    </div>
  </div>
);

const ImageEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <div className="space-y-1">
      <Label className="text-xs">URL Immagine</Label>
      <Input value={data.src} onChange={(e) => onChange({ ...data, src: e.target.value })} placeholder="https://esempio.com/immagine.jpg" data-testid="block-image-src" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">Testo alternativo</Label>
        <Input value={data.alt} onChange={(e) => onChange({ ...data, alt: e.target.value })} className="h-8 text-xs" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Larghezza (%)</Label>
        <Input type="number" min="20" max="100" value={data.width} onChange={(e) => onChange({ ...data, width: e.target.value })} className="h-8 text-xs" />
      </div>
    </div>
    <div className="space-y-1">
      <Label className="text-xs">Didascalia (opzionale)</Label>
      <Input value={data.caption} onChange={(e) => onChange({ ...data, caption: e.target.value })} placeholder="Didascalia sotto l'immagine" className="h-8 text-xs" />
    </div>
  </div>
);

const ButtonEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">Testo</Label>
        <Input value={data.text} onChange={(e) => onChange({ ...data, text: e.target.value })} data-testid="block-button-text" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Link (URL)</Label>
        <Input value={data.url} onChange={(e) => onChange({ ...data, url: e.target.value })} placeholder="https://..." />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <ColorInput label="Sfondo" value={data.bgColor} onChange={(v) => onChange({ ...data, bgColor: v })} />
      <ColorInput label="Testo" value={data.textColor} onChange={(v) => onChange({ ...data, textColor: v })} />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">Allineamento</Label>
        <Select value={data.align} onValueChange={(v) => onChange({ ...data, align: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Sinistra</SelectItem>
            <SelectItem value="center">Centro</SelectItem>
            <SelectItem value="right">Destra</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Arrotondamento (px)</Label>
        <Input type="number" min="0" max="50" value={data.borderRadius} onChange={(e) => onChange({ ...data, borderRadius: e.target.value })} className="h-8 text-xs" />
      </div>
    </div>
  </div>
);

const SeparatorEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">Stile</Label>
        <Select value={data.style} onValueChange={(v) => onChange({ ...data, style: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="line">Linea</SelectItem>
            <SelectItem value="space">Spazio</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Spaziatura (px)</Label>
        <Input type="number" min="5" max="80" value={data.spacing} onChange={(e) => onChange({ ...data, spacing: e.target.value })} className="h-8 text-xs" />
      </div>
    </div>
    {data.style === 'line' && (
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Spessore (px)</Label>
          <Input type="number" min="1" max="5" value={data.height} onChange={(e) => onChange({ ...data, height: e.target.value })} className="h-8 text-xs" />
        </div>
        <ColorInput label="Colore" value={data.color} onChange={(v) => onChange({ ...data, color: v })} />
      </div>
    )}
  </div>
);

const ColumnsEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <div className="space-y-1">
      <Label className="text-xs">Proporzioni</Label>
      <Select value={data.ratio} onValueChange={(v) => onChange({ ...data, ratio: v })}>
        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="50-50">50% - 50%</SelectItem>
          <SelectItem value="60-40">60% - 40%</SelectItem>
          <SelectItem value="40-60">40% - 60%</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1">
      <Label className="text-xs">Colonna Sinistra</Label>
      <Textarea value={data.leftContent} onChange={(e) => onChange({ ...data, leftContent: e.target.value })} rows={2} className="text-xs" />
    </div>
    <div className="space-y-1">
      <Label className="text-xs">Colonna Destra</Label>
      <Textarea value={data.rightContent} onChange={(e) => onChange({ ...data, rightContent: e.target.value })} rows={2} className="text-xs" />
    </div>
    <p className="text-[10px] text-muted-foreground">Supporta HTML: &lt;strong&gt;, &lt;em&gt;, &lt;br/&gt;</p>
  </div>
);

const EDITORS = {
  header: HeaderEditor,
  text: TextEditor,
  image: ImageEditor,
  button: ButtonEditor,
  separator: SeparatorEditor,
  columns: ColumnsEditor,
};

const EmailBlockEditor = ({ block, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, dragAttributes, dragListeners }) => {
  const Icon = ICONS[block.type] || Type;
  const Editor = EDITORS[block.type];

  return (
    <div className="group border rounded-lg bg-card transition-all hover:border-primary/30" data-testid={`email-block-${block.id}`}>
      {/* Block Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 rounded-t-lg">
        <button {...dragAttributes} {...dragListeners} className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground hover:text-foreground touch-none" data-testid={`drag-handle-${block.id}`}>
          <GripVertical className="w-4 h-4" />
        </button>
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium flex-1">{LABELS[block.type]}</span>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onMoveUp} disabled={isFirst}><ChevronUp className="w-3 h-3" /></Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onMoveDown} disabled={isLast}><ChevronDown className="w-3 h-3" /></Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" onClick={onDelete} data-testid={`delete-block-${block.id}`}><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
      {/* Block Content Editor */}
      <div className="p-3">
        {Editor && <Editor data={block.data} onChange={(newData) => onChange(block.id, newData)} />}
      </div>
    </div>
  );
};

export default EmailBlockEditor;
